import axios from 'axios'
import { useEffect, useState } from 'react'

import { CarData, CollapsedCar } from '../../features/car/types'
import { logger } from '../../lib/logger'

type ResponseWithData<T> = {
  status: 'success'
  data: T
}

type ResponseWithError = {
  status: 'error'
  message?: string
  debug?: {
    code: string
    body: {
      errors: { code: string; title: string }[]
    }
  }
}

type Response = ResponseWithError | ResponseWithData<{ cars: CollapsedCar[] }>

export const getCollapsedCars = (url: string): Promise<CollapsedCar[]> =>
  axios
    .get<Response>(url)
    .then((axiosResponse) => axiosResponse.data)
    .then((apiResponse) => {
      if (apiResponse.status === 'error') throw new Error(apiResponse.message || 'Unspecified Error')
      return apiResponse.data
    })
    .then((data) => data.cars)

const createCollapsedCarsFromDealers = (carData: CarData) =>
  carData.dealers.map((dealer) => ({ identity: carData.vinRaw, dealerId: dealer.id, id: carData.id }))

/**
 * https://kodixauto.atlassian.net/wiki/spaces/NW/pages/3230728212
 */
export const useCollapsedCars = (hasCollapsedCars: boolean, url: string, carData: CarData) => {
  const [data, setData] = useState<CollapsedCar[] | null>(null)

  useEffect(() => {
    if (hasCollapsedCars) {
      getCollapsedCars(url)
        .then(setData)
        .catch((e) => {
          logger.error(`[Api/GetCollapsedCars]: ${e.message}, creating collapsed cars from dealers`)
          setData(createCollapsedCarsFromDealers(carData))
        })
    } else {
      setData(createCollapsedCarsFromDealers(carData))
    }
  }, [url, hasCollapsedCars])

  return data
}
